import { useState } from 'react';
import { orders } from '@/mocks/orders';
import type { Order } from '@/mocks/orders';

const paymentStatusOptions = [
  { key: 'bekleniyor', label: 'Ödeme Bekleniyor', color: 'bg-status-yellow' },
  { key: 'tahsil_edildi', label: 'Tahsil Edildi', color: 'bg-status-green' },
  { key: 'kismi_tahsil', label: 'Kısmi Tahsil Edildi', color: 'bg-status-orange' },
  { key: 'iade', label: 'İade Oldu', color: 'bg-status-red' },
  { key: 'iptal', label: 'İptal Edildi', color: 'bg-status-gray' },
];

function getPaymentColor(status: string): string {
  return paymentStatusOptions.find(s => s.key === status)?.color || 'bg-stone-300';
}

function getPaymentLabel(status: string): string {
  return paymentStatusOptions.find(s => s.key === status)?.label || 'Belirsiz';
}

const allFinanceOrders = orders.filter(o =>
  o.paymentType === 'kapida_odeme' || o.paymentStatus === 'bekleniyor' || o.paymentStatus === 'tahsil_edildi'
);

export default function FinancePage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  let filtered = [...allFinanceOrders];

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(o =>
      o.customerName.toLowerCase().includes(q) ||
      o.id.toLowerCase().includes(q) ||
      o.phone.includes(q) ||
      o.city.toLowerCase().includes(q)
    );
  }

  if (statusFilter) {
    filtered = filtered.filter(o => o.paymentStatus === statusFilter);
  }

  const totalAmount = allFinanceOrders.reduce((s, o) => s + o.amount, 0);
  const pendingCOD = allFinanceOrders.filter(o => o.paymentType === 'kapida_odeme' && o.paymentStatus === 'bekleniyor').reduce((s, o) => s + o.amount, 0);
  const collected = allFinanceOrders.filter(o => o.paymentStatus === 'tahsil_edildi').reduce((s, o) => s + o.amount, 0);
  const returned = allFinanceOrders.filter(o => o.paymentStatus === 'iade').reduce((s, o) => s + o.amount, 0);
  const todayCollected = allFinanceOrders.filter(o => o.paymentStatus === 'tahsil_edildi' && o.orderDate === '2026-06-17').reduce((s, o) => s + o.amount, 0);
  const monthCollected = collected;
  const problemCount = filtered.filter(o => o.paymentStatus === 'bekleniyor' && (
    o.status === 'sorunlu' || o.isLate
  )).length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-stone-900">Cari Takip</h2>
          <p className="text-sm text-stone-500 mt-0.5">Kapıda ödeme ve tahsilat kontrolü</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-white text-sm font-medium rounded-lg hover:bg-gold-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-download-line" />Cari Döküm İndir
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-5">
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-lg md:text-xl font-bold text-stone-900">{totalAmount.toLocaleString('tr-TR')} ₺</p>
          <p className="text-xs text-stone-500 mt-1">Toplam Sipariş Tutarı</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-lg md:text-xl font-bold text-status-orange">{pendingCOD.toLocaleString('tr-TR')} ₺</p>
          <p className="text-xs text-stone-500 mt-1">Bekleyen Kapıda Ödeme</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-lg md:text-xl font-bold text-status-green">{collected.toLocaleString('tr-TR')} ₺</p>
          <p className="text-xs text-stone-500 mt-1">Tahsil Edilen</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-lg md:text-xl font-bold text-status-red">{returned.toLocaleString('tr-TR')} ₺</p>
          <p className="text-xs text-stone-500 mt-1">İade Tutarı</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-lg md:text-xl font-bold text-status-green">{todayCollected.toLocaleString('tr-TR')} ₺</p>
          <p className="text-xs text-stone-500 mt-1">Bugün Tahsil Edilen</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-lg md:text-xl font-bold text-stone-900">{monthCollected.toLocaleString('tr-TR')} ₺</p>
          <p className="text-xs text-stone-500 mt-1">Bu Ay Tahsil Edilen</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-lg md:text-xl font-bold text-status-red">{problemCount}</p>
          <p className="text-xs text-stone-500 mt-1">Sorunlu Ödemeler</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input
            type="text"
            placeholder="Müşteri, sipariş ID veya telefon ile ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-700 cursor-pointer focus:outline-none focus:border-gold-400"
        >
          <option value="">Tüm Tahsilat Durumları</option>
          {paymentStatusOptions.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Finance Table */}
      <div className="bg-white rounded-xl border border-stone-200/80 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50/50">
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Müşteri</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Telefon</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Sipariş ID</th>
                <th className="text-right text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Tutar</th>
                <th className="text-center text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Ödeme Tipi</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Tahsilat Durumu</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Sipariş Durumu</th>
                <th className="text-right text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Net Kalan</th>
                <th className="text-center text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Aksiyon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filtered.map((order) => {
                const netRemaining = order.paymentStatus === 'tahsil_edildi' ? 0 : order.amount;
                return (
                  <tr key={order.id} className="hover:bg-stone-50/50 transition-colors group">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-stone-800">{order.customerName}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-stone-500 whitespace-nowrap">{order.phone.replace('90', '0')}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono font-semibold text-stone-800">{order.id}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-semibold text-stone-800">{order.amount.toLocaleString('tr-TR')} ₺</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                        {order.paymentType === 'kapida_odeme' ? 'Kapıda' : order.paymentType === 'havale' ? 'Havale' : 'K.Kartı'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                        order.paymentStatus === 'tahsil_edildi' ? 'bg-status-green/10 text-status-green' :
                        order.paymentStatus === 'bekleniyor' ? 'bg-status-orange/10 text-status-orange' :
                        order.paymentStatus === 'iade' ? 'bg-status-red/10 text-status-red' :
                        'bg-status-gray/10 text-status-gray'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${getPaymentColor(order.paymentStatus)}`} />
                        {getPaymentLabel(order.paymentStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-stone-500">{order.productionStage}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-semibold ${netRemaining > 0 ? 'text-status-orange' : 'text-status-green'}`}>
                        {netRemaining > 0 ? netRemaining.toLocaleString('tr-TR') + ' ₺' : '0 ₺'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {order.paymentStatus === 'bekleniyor' && (
                          <button
                            className="w-7 h-7 flex items-center justify-center rounded-md bg-status-green/10 text-status-green hover:bg-status-green/20 transition-colors cursor-pointer"
                            title="Ödeme Alındı İşaretle"
                          >
                            <i className="ri-bank-card-line text-sm" />
                          </button>
                        )}
                        {order.paymentStatus !== 'iade' && order.paymentStatus !== 'iptal' && (
                          <button
                            className="w-7 h-7 flex items-center justify-center rounded-md bg-status-red/10 text-status-red hover:bg-status-red/20 transition-colors cursor-pointer"
                            title="İade İşaretle"
                          >
                            <i className="ri-arrow-go-back-line text-sm" />
                          </button>
                        )}
                        <a
                          href={`https://wa.me/${order.phone}`}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-green-50 text-green-600 hover:bg-green-100 transition-colors cursor-pointer"
                          title="WhatsApp'tan Yaz"
                        >
                          <i className="ri-whatsapp-line text-sm" />
                        </a>
                        <button
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-stone-100 text-stone-500 hover:bg-stone-200 transition-colors cursor-pointer"
                          title="Not Ekle"
                        >
                          <i className="ri-sticky-note-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}